// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// 🔴 2026-10-23 · O ECRÃ BRANCO AO ABRIR UMA ABA — a rede das janelas diferidas
// ============================================================================
//
// 📗 Cliente: *«por vezes após dar login, ao abrir alguma aba, fico com tela branca, precisando
//    dar F5»*. A causa: cada janela carrega o SEU pedaço de código só quando abre (`janelaDiferida`,
//    em `Mapa.tsx`), e o nome do pedaço muda a cada versão publicada — várias por dia. Uma página
//    aberta antes de uma publicação pede um pedaço que já não existe, o `import()` falha, e SEM
//    `ErrorBoundary` nenhum o React desmontava a aplicação inteira: branco. O F5 trazia a versão
//    nova e resolvia — daí o «por vezes».
//
// ➡️ Duas camadas:
//    1. a falha do carregamento recarrega a página UMA vez (marca na sessão — nunca em ciclo);
//    2. uma rede por janela: se ainda falhar, a JANELA diz que não abriu e oferece recarregar, e o
//       resto da aplicação continua de pé.
// ============================================================================

import { Component, type ReactNode } from 'react';

/** A marca que impede recarregar em ciclo: só uma tentativa por sessão do separador. */
export const MARCA_DA_RECARGA = 'mpt-recarregou-por-versao-nova';

/** As duas operações do navegador, injectáveis para as provas. */
export interface Navegador {
  readonly lerMarca: () => string | null;
  readonly gravarMarca: (v: string) => void;
  readonly apagarMarca: () => void;
  readonly recarregar: () => void;
}

const NAVEGADOR: Navegador = {
  lerMarca: () => {
    try {
      return sessionStorage.getItem(MARCA_DA_RECARGA);
    } catch {
      return null;
    }
  },
  gravarMarca: (v) => {
    try {
      sessionStorage.setItem(MARCA_DA_RECARGA, v);
    } catch {
      /* sem armazenamento (navegação privada): recarrega na mesma, uma vez */
    }
  },
  apagarMarca: () => {
    try {
      sessionStorage.removeItem(MARCA_DA_RECARGA);
    } catch {
      /* idem */
    }
  },
  recarregar: () => window.location.reload(),
};

/**
 * Embrulha o `import()` de uma janela: ao falhar pela primeira vez na sessão, recarrega a página
 * (a versão nova traz os pedaços certos) e fica à espera; à segunda, deixa a falha seguir para a
 * rede da janela. Um carregamento que corre bem apaga a marca — a próxima publicação volta a ter
 * direito à sua recarga.
 */
export function carregarComRecarga<T>(carregar: () => Promise<T>, nav: Navegador = NAVEGADOR): () => Promise<T> {
  return () =>
    carregar().then(
      (m) => {
        nav.apagarMarca();
        return m;
      },
      (erro: unknown) => {
        if (nav.lerMarca() === null) {
          nav.gravarMarca(String(Date.now()));
          nav.recarregar();
          return new Promise<T>(() => {
            /* a página vai recarregar — nunca resolve */
          });
        }
        throw erro;
      },
    );
}

/** A rede de UMA janela: uma falha fica nela, e a aplicação continua de pé. */
export class RedeDaJanela extends Component<{ readonly children: ReactNode }, { readonly falhou: boolean }> {
  override state = { falhou: false };

  static getDerivedStateFromError(): { falhou: boolean } {
    return { falhou: true };
  }

  override render(): ReactNode {
    if (!this.state.falhou) return this.props.children;
    return (
      <div className="janela janela--falhou" role="alert">
        <p>Esta janela não abriu — provavelmente há uma versão nova da aplicação.</p>
        <button type="button" className="btn" onClick={() => window.location.reload()}>
          Recarregar
        </button>
      </div>
    );
  }
}
